/**
 * Theme context: 'light' | 'dark' | 'system'.
 *
 * - Persists the chosen mode under THEME_STORAGE_KEY ('theme').
 * - Applies/removes the `.dark` class on <html> — all the semantic Tailwind
 *   tokens flip via the CSS variables in index.css.
 * - In 'system' mode it tracks the OS `prefers-color-scheme` live.
 *
 * Both the stored mode and the OS preference are external stores read with
 * `useSyncExternalStore` (web plan W4.2): the server renders 'system' with a
 * light OS (it knows neither), and so does the render that hydrates its HTML;
 * the stored mode follows right after. In a `createRoot` tree (the SPA) the
 * first render already has it. The `.dark` class itself never follows the
 * rendered value: index.html's inline script sets it before the first paint,
 * and the effect below re-applies what the stores say in the browser — so the
 * server's "light" never takes it off, not even for one frame. A change of
 * mode in another tab shows here too (storage event).
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react';
import { THEME_STORAGE_KEY } from '@/constants/config';
import { createStorageStore, type StorageStore } from '@/lib/storageStore';

export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

interface ThemeContextType {
  /** The user's preference (may be 'system'). */
  mode: ThemeMode;
  /** What is actually applied right now. */
  resolved: ResolvedTheme;
  setMode: (mode: ThemeMode) => void;
  /** Flip to the opposite of the currently resolved theme. */
  toggle: () => void;
}

function readStoredMode(): ThemeMode {
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  } catch {
    // ignore
  }
  return 'system';
}

function writeStoredMode(mode: ThemeMode): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, mode);
  } catch {
    // ignore
  }
}

const DARK_QUERY = '(prefers-color-scheme: dark)';

function systemPrefersDark(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(DARK_QUERY).matches
  );
}

/**
 * Track OS preference changes (only matters while in 'system' mode, but the
 * listener is cheap and keeps `systemDark` always current).
 */
function subscribeSystemDark(onChange: () => void): () => void {
  if (typeof window.matchMedia !== 'function') return () => {};
  const mql = window.matchMedia(DARK_QUERY);
  mql.addEventListener('change', onChange);
  return () => mql.removeEventListener('change', onChange);
}

/** The server cannot know the OS preference; the hydration render uses this too. */
const serverSystemDark = (): boolean => false;

function resolveTheme(mode: ThemeMode, systemDark: boolean): ResolvedTheme {
  return mode === 'system' ? (systemDark ? 'dark' : 'light') : mode;
}

function createModeStore(): StorageStore<ThemeMode> {
  return createStorageStore<ThemeMode>({
    key: THEME_STORAGE_KEY,
    read: readStoredMode,
    write: writeStoredMode,
    serverSnapshot: 'system',
  });
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [modeStore] = useState(createModeStore);
  const mode = useSyncExternalStore(
    modeStore.subscribe,
    modeStore.getSnapshot,
    modeStore.getServerSnapshot,
  );
  const systemDark = useSyncExternalStore(subscribeSystemDark, systemPrefersDark, serverSystemDark);

  const resolved = resolveTheme(mode, systemDark);

  // Apply the .dark class on <html> — from the browser's stores, not from
  // `resolved`: while hydrating, `resolved` is the server's ('system', light),
  // and applying it would strip the class the inline script set until the
  // re-render put it back.
  useEffect(() => {
    document.documentElement.classList.toggle(
      'dark',
      resolveTheme(modeStore.getSnapshot(), systemPrefersDark()) === 'dark',
    );
  }, [modeStore, resolved]);

  const setMode = useCallback((next: ThemeMode) => modeStore.set(next), [modeStore]);

  const toggle = useCallback(() => {
    setMode(resolved === 'dark' ? 'light' : 'dark');
  }, [resolved, setMode]);

  const value = useMemo<ThemeContextType>(
    () => ({ mode, resolved, setMode, toggle }),
    [mode, resolved, setMode, toggle],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
